import { Dictionary } from 'src/app/core/model/dictionary';
import { HelperUtils } from 'src/app/core/utility/helper-utils';
import { ColumnReorderEvent, ColumnVisibilityChangeEvent, DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GroupResult, groupBy, State } from '@progress/kendo-data-query';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { CommunityServiceCategoryService } from 'src/app/core/service/community-service-category.service';
import { DictionaryService } from 'src/app/core/service/dictionary.service';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { sortBy, cloneDeep, remove, union } from 'lodash';
import { CommunityProviderSearchResult } from 'src/app/referral/model/community-provider-search-result';
import { LocalStorageService } from 'ngx-webstorage';
import { SettingService } from 'src/app/core/service/setting.service';
import { SessionSetting } from 'src/app/core/model/session-setting';
import { TranslateService } from '@ngx-translate/core';
import { CommunityProviderProgramSearchListService } from 'src/app/core/service/grid-data/grid-data-community-search.service';
import { debounceTime } from 'rxjs/operators';
import { MemberService } from 'src/app/core/service/member.service';
import { Member } from 'src/app/member/model/member';
import { Organization } from 'src/app/core/model/organization';
import { Filter } from 'src/app/core/filter/filter';
import { FilterService } from 'src/app/core/filter/filter.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { ColumnSettings } from 'src/app/core/model/column-settings';
import { GridResizeEvent, GridSettings } from 'src/app/core/model/grid-settings';
import { GridStatePersistingService } from 'src/app/core/service/grid-state-persisting';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';
import { UserService } from 'src/app/core/service/user.service';
import
{
  TMEventSearchServices,
  TMEventType, TagManagerEventService
} from 'src/app/core/service/tag-manager.service';
import { NGXLogger } from 'ngx-logger';
import { GridQueryUtils } from 'src/app/core/utility/grid-query-utils';
import { MultiSelectComponent } from '@progress/kendo-angular-dropdowns';

@Component({
  standalone: false,
  selector: 'app-community-search',
  templateUrl: './community-search.component.html',
  styleUrls: ['./community-search.component.scss']
})
export class CommunitySearchComponent implements OnInit
{
  @ViewChild('serviceCategoryControl') public serviceCategoriesControl: MultiSelectComponent;
  @ViewChild('splitter', { read: ElementRef }) public splitter: ElementRef;

  @Input() public memberId = 0;
  @Input() public member: Member;
  @Input() public serviceCategories = [];
  @Input() public excludedServices = [];
  @Input() public defaultResourceStatuses = [];
  @Input() public referralReassign = false;
  @Input() public allowSelection = false;

  @Output() public selectedServices = new EventEmitter<any>();

  public searchTerm = '';
  public searchForm: any;
  public selectedResourcesIds: number[] = [];
  public consentToReferIds: number[] = [];
  public filterDialogOpened = false;
  public filterSelected: Filter;
  public servicesOpened = false;
  public isPrint = false;
  public isEmail = false;
  public isSMS = false;
  public pinnedResourcesEnabled = false;
  public emailConfirmationOpened = false;
  public smsConfirmationOpened = false;
  public emailForm: any;
  public phoneNumberMask: string;
  public communityPortalEnabled = false;
  public allowSystemFilters = false;
  public saveFilterButtonFocused = false;

  public allFilters: any[] | GroupResult[] = [];
  public allOrganizations = [];
  public allSubmittingOrganizations = [];
  public allLanguages = [];
  public allServiceCategories = [];
  public allServedCities = [];
  public allServedCounties = [];
  public allServedZipCodes = [];
  public allTargetedDemographics = [];
  public allTargetedPopulations = [];
  public allResourceStatuses = [];
  public allTaxonomies = [];
  public allEnhancedServices = [];

  public previousServiceCategories = [];
  public previousCitiesServed = [];
  public previousCountiesServed = [];
  public previousZipCodesServed = [];
  public previousOrganizations = [];
  public previousSubmittingOrganizations = [];
  public previousLanguages = [];
  public previousTargetedPopulations = [];
  public previousTargetedDemographics = [];
  public previousResourceStatuses = [];
  public previousTaxonomies = [];
  public previousEnhancedServices = [];

  public organizationsFiltered: Organization[] = [];
  public submittingOrganizationsFiltered: Organization[] = [];
  public filtersFiltered: any[] | GroupResult[] = [];
  public servedCitiesFiltered = [];
  public servedCountiesFiltered = [];
  public servedZipCodesFiltered = [];
  public targetedDemographicsFiltered = [];
  public targetedPopulationsFiltered = [];
  public languagesFiltered = [];
  public resourceStatusesFiltered = [];
  public taxonomiesFiltered = [];
  public enhancedServicesFiltered = [];

  public searchByDistanceEnabled = false;
  public maxDistanceDefault: number;
  public distanceUnitDefault: string;
  public groupedServiceCategories: GroupResult[] = [];
  public categoriesFiltered: GroupResult[] = [];
  public providers: CommunityProviderSearchResult;
  public mileText: string;
  public kmText: string;
  public systemFiltersText: string;
  public privateFiltersText: string;
  public loaded = false;
  public patientLabel: any = {};
  public total: number;
  public state: State = {
    skip: 0,
    filter: {
      logic: 'and',
      filters: []
    },
    sort: []
  };
  public pageSizes = [5, 10, 25, 50, 100];
  public enableAdvanced = true;
  public gridView: GridDataResult;
  public gridData: any = {};
  public virtualLookups: any = { itemHeight: 30 };
  public columnsConfig: ColumnSettings[];
  public selectedEmailAddress = '';
  public selectedPhoneNumber = '';

  private paneSizeDefault: string;
  private searchableCommunityResources = [];
  private debounceSearchTime = 500;

  constructor(
    private dictionaryService: DictionaryService,
    private authenticationService: AuthenticationService,
    private fb: UntypedFormBuilder,
    private memberService: MemberService,
    private settingService: SettingService,
    private storageService: LocalStorageService,
    private translateService: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private filterService: FilterService,
    private appConfigService: AppConfigService,
    private communityServiceCategoryService: CommunityServiceCategoryService,
    private communityProviderProgramSearchList: CommunityProviderProgramSearchListService,
    private gridStatePersistingService: GridStatePersistingService,
    private messengerService: MessengerService,
    private userService: UserService,
    private organizationService: OrganizationService,
    private tmService: TagManagerEventService,
    private logger: NGXLogger)
  {
    this.setPageSize();
    this.paneSizeDefault = storageService.retrieve('community-services-splitter-size');
    this.providers = new CommunityProviderSearchResult();
  }

  /**
   * Provide loading property from Service.
   */
  public get loading(): boolean
  {
    return this.communityProviderProgramSearchList.loading;
  }

  public get email(): UntypedFormControl
  {
    return this.emailForm.get('email');
  }

  public get sms(): UntypedFormControl
  {
    return this.emailForm.get('sms');
  }

  /**
   * Getter for form control field filterByDistance.
   */
  public get filterByDistance(): UntypedFormControl
  {
    return this.searchForm.get('filterByDistance');
  }

  /**
   * Getter for form control field distance.
   */
  public get maxDistance(): UntypedFormControl
  {
    return this.searchForm.get('maxDistance');
  }

  /**
   * Getter for form control field categories.
   */
  public get categories(): UntypedFormControl
  {
    return this.searchForm.get('categories');
  }

  public get filter(): UntypedFormControl
  {
    return this.searchForm.get('filter');
  }

  /**
   * Getter for form control field name.
   */
  public get name(): UntypedFormControl
  {
    return this.searchForm.get('name');
  }

  public get organizations(): UntypedFormControl
  {
    return this.searchForm.get('organizations');
  }

  public get resourceStatuses(): UntypedFormControl
  {
    return this.searchForm.get('resourceStatuses');
  }

  public get taxonomies(): UntypedFormControl
  {
    return this.searchForm.get('taxonomies');
  }

  public get servedCities(): UntypedFormControl
  {
    return this.searchForm.get('servedCities');
  }

  public get servedCounties(): UntypedFormControl
  {
    return this.searchForm.get('servedCounties');
  }

  public get servedZipCodes(): UntypedFormControl
  {
    return this.searchForm.get('servedZipCodes');
  }

  public get submittingOrganizations(): UntypedFormControl
  {
    return this.searchForm.get('submittingOrganizations');
  }

  public get targetedDemographics(): UntypedFormControl
  {
    return this.searchForm.get('targetedDemographics');
  }

  public get targetedPopulations(): UntypedFormControl
  {
    return this.searchForm.get('targetedPopulations');
  }

  public get languages(): UntypedFormControl
  {
    return this.searchForm.get('languages');
  }

  public get enhancedServices(): UntypedFormControl
  {
    return this.searchForm.get('enhancedServices');
  }

  public get acceptingNewPatients(): UntypedFormControl
  {
    return this.searchForm.get('acceptingNewPatients');
  }

  public get acceptingSelfReferrals(): UntypedFormControl
  {
    return this.searchForm.get('acceptingSelfReferrals');
  }

  public get ableToConductScreenings(): UntypedFormControl
  {
    return this.searchForm.get('ableToConductScreenings');
  }

  public get informationalOnly(): UntypedFormControl
  {
    return this.searchForm.get('informationalOnly');
  }

  public get saveFilterVisible(): boolean
  {
    return this.allowSystemFilters || this.filter.value === undefined || this.filter.value === null ||
      (this.filter.value && this.filter.value.category === 'CommunityService');
  }

  public get showFilterBtnActions(): boolean
  {
    return this.name.value ||
      (this.categories.value && this.categories.value.length > 0) ||
      (this.organizations.value && this.organizations.value.length > 0) ||
      (this.submittingOrganizations.value && this.submittingOrganizations.value.length > 0) ||
      (this.servedCities.value && this.servedCities.value.length > 0) ||
      (this.resourceStatuses.value && this.resourceStatuses.value.length > 0) ||
      (this.taxonomies.value && this.taxonomies.value.length > 0) ||
      (this.servedCounties.value && this.servedCounties.value.length > 0) ||
      (this.servedZipCodes.value && this.servedZipCodes.value.length > 0) ||
      (this.targetedDemographics.value && this.targetedDemographics.value.length > 0) ||
      (this.targetedPopulations.value && this.targetedPopulations.value.length > 0) ||
      (this.languages.value && this.languages.value.length > 0) ||
      (this.enhancedServices.value && this.enhancedServices.value.length > 0) ||
      (this.acceptingNewPatients.value !== null) ||
      (this.acceptingSelfReferrals.value !== null) ||
      (this.ableToConductScreenings.value !== null) ||
      (this.informationalOnly.value !== null);
  }

  public get paneSize(): string
  {
    return this.paneSizeDefault;
  }

  public get selectedResourcesCount(): number
  {
    return this.selectedResourcesIds.length;
  }

  public get selectedReferralsCount(): number
  {
    return this.consentToReferIds.length;
  }

  public set paneSize(newSize: string)
  {
    this.paneSizeDefault = newSize;
    this.storageService.store('community-services-splitter-size', newSize);
  }

  public ngOnInit(): void
  {
    this.translateService.get(['DISTANCE-UNIT-MILE', 'DISTANCE-UNIT-KM', 'SYSTEM-FILTERS', 'PRIVATE-FILTERS']).subscribe({
      next: (res) =>
      {
        this.mileText = res['DISTANCE-UNIT-MILE'];
        this.kmText = res['DISTANCE-UNIT-KM'];
        this.systemFiltersText = res['SYSTEM-FILTERS'];
        this.privateFiltersText = res['PRIVATE-FILTERS'];
      }
    });

    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting: SessionSetting) =>
      {
        this.allowSystemFilters = this.authenticationService.currentUser.role === 'ROLE_OPERATOR';
        this.communityPortalEnabled = sessionSetting.communityPortalEnabled;
        this.maxDistanceDefault = sessionSetting.communityProviderMaximumDistance;
        this.distanceUnitDefault = sessionSetting.providerDistanceDisplay;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
        this.searchableCommunityResources =
          sessionSetting.searchableCommunityResources ? sessionSetting.searchableCommunityResources.split(',') : [];
        this.pinnedResourcesEnabled = (sessionSetting.pinnedResourcesRoles !== '') ?
          this.userService.isRoleInList(this.authenticationService.currentUser.role, sessionSetting.pinnedResourcesRoles) : false;
        this.phoneNumberMask = sessionSetting.phoneMask;
        this.defaultResourceStatuses = sessionSetting.defaultResourceStatuses ? sessionSetting.defaultResourceStatuses.split(',') : [];

        this.initGridColumns();
        this.loadFilters();
        this.loadOrganizations();
        this.loadLanguages();
        this.loadTargeted();
        this.loadAreaServed();
        this.loadResourceStatuses();
        this.loadTaxonomies();
        this.loadServiceCategories();
        this.loadEnhancedServices();
      }
    });
  }

  public buildForm(): void
  {
    this.clearGrid();

    const filterByDistance = this.storageService.retrieve('filterByDistance');
    const maxDistance = this.storageService.retrieve('maxDistance');

    const resourceStatusToList = this.allResourceStatuses.filter(obj => this.defaultResourceStatuses.includes(obj.id.toString()));

    this.searchForm = this.fb.group({
      filter: [{ value: null, disabled: false }],
      name: [{ value: null, disabled: false }],
      categories: [{ value: null, disabled: false }],
      organizations: [{ value: null, disabled: false }],
      submittingOrganizations: [{ value: null, disabled: false }],
      resourceStatuses: [{ value: resourceStatusToList, disabled: false }],
      taxonomies: [{ value: null, disabled: false }],
      servedCities: [{ value: null, disabled: false }],
      servedCounties: [{ value: null, disabled: false }],
      servedZipCodes: [{ value: null, disabled: false }],
      targetedDemographics: [{ value: null, disabled: false }],
      targetedPopulations: [{ value: null, disabled: false }],
      languages: [{ value: null, disabled: false }],
      enhancedServices: [{ value: null, disabled: false }],
      acceptingNewPatients: [{ value: null, disabled: false }],
      acceptingSelfReferrals: [{ value: null, disabled: false }],
      ableToConductScreenings: [{ value: null, disabled: false }],
      informationalOnly: [{ value: null, disabled: false }],
      filterByDistance: filterByDistance !== null ? filterByDistance : true,
      // 25% of the total distance set
      maxDistance: maxDistance !== null ? Math.ceil(maxDistance) : Math.ceil(this.maxDistanceDefault * 0.25)
    });

    // Disable or enable category control (Use case is the referral reassign)
    if (this.referralReassign)
    {
      this.categories.disable();
      this.filter.disable();
    }
    else
    {
      this.categories.enable();
      this.filter.enable();
    }

    this.searchForm.get('filter').valueChanges.subscribe({
      next: (value: Filter) =>
      {
        if (value)
        {
          this.loadControlsFromFilters();
        }
      }
    });

    this.searchForm.get('name').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('categories').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('organizations').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('submittingOrganizations').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('servedCities').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('resourceStatuses').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('taxonomies').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('servedCounties').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('servedZipCodes').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('targetedPopulations').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('targetedDemographics').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('languages').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('enhancedServices').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('acceptingNewPatients').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('acceptingSelfReferrals').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('ableToConductScreenings').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('informationalOnly').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    this.searchForm.get('filterByDistance').valueChanges.pipe(debounceTime(this.debounceSearchTime)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    // Run through a debounce to not call the server too often
    this.searchForm.get('maxDistance').valueChanges.pipe(debounceTime(500)).subscribe({
      next: () => { this.searchPrograms(); }
    });

    // Get the patient address
    if (this.memberId > 0)
    {
      if (this.member !== undefined)
      {
        this.initMember(this.member);
      }
      else
      {
        this.memberService.getMember(this.memberId).subscribe({
          next: (member) =>
          {
            this.initMember(member);

          },
          error: (errorResponse) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      }
    }
    else
    {
      this.loaded = true;
    }

    // update splitter bar once we are sure the page is loaded
    setTimeout(() => this.updateSplitterBar());
  }

  public pageChange(event: any): void
  {
    if (this.filter.value === undefined || this.filter.value === null)
    {
      this.storageService.store('community-services-page-size', event.take);
    }
  }

  /**
   * Clear all filters.
   */
  public clearFilters(): void
  {
    this.clearGrid();
    this.setPageSize();

    this.filter.setValue(null);
    this.name.setValue(null);
    // Clear only if enabled
    if (this.categories.enabled)
    {
      this.categories.setValue(null);
    }
    this.organizations.setValue(null);
    this.submittingOrganizations.setValue(null);
    this.resourceStatuses.setValue(null);
    this.taxonomies.setValue(null);
    this.servedCities.setValue(null);
    this.servedCounties.setValue(null);
    this.servedZipCodes.setValue(null);
    this.targetedDemographics.setValue(null);
    this.targetedPopulations.setValue(null);
    this.languages.setValue(null);
    this.enhancedServices.setValue(null);
    this.acceptingNewPatients.setValue(null);
    this.acceptingSelfReferrals.setValue(null);
    this.ableToConductScreenings.setValue(null);
    this.informationalOnly.setValue(null);

    this.previousServiceCategories = [];
    this.previousCitiesServed = [];
    this.previousCountiesServed = [];
    this.previousZipCodesServed = [];
    this.previousOrganizations = [];
    this.previousSubmittingOrganizations = [];
    this.previousLanguages = [];
    this.previousEnhancedServices = [];
    this.previousTargetedPopulations = [];
    this.previousTargetedDemographics = [];
    this.previousResourceStatuses = [];
    this.previousTaxonomies = [];
  }

  public formatMaxDistanceLabel(maxDistance: number): any
  {
    const unit = this.distanceUnitDefault === 'MILES' ? this.mileText : this.kmText;
    return `<= ${maxDistance} ${unit}` + (maxDistance > 1 ? 's' : '');
  }

  public formatDistanceLabel(maxDistance: number): any
  {
    const unit = this.distanceUnitDefault === 'MILES' ? this.mileText : this.kmText;
    return `${unit}` + (maxDistance > 1 ? 's' : '');
  }

  /**
   * Get provider programs.
   */
  public searchPrograms(): void
  {
    this.state.skip = 0;
    this.clearAllSelected();
    this.loadServices();
  }

  public closeServicesInfo(): void
  {
    this.servicesOpened = false;
    this.selectedEmailAddress = undefined;
    this.selectedPhoneNumber = undefined;
    this.isEmail = false;
    this.isPrint = false;
    this.isSMS = false;
  }

  /**
   * Delete saved filter
   *
   * @param filterId Filter id to delete.
   */
  public deleteFilter(filterId: number): void
  {
    this.filterService.deleteFilter(filterId).subscribe({
      next: () =>
      {
        this.loadFilters();
        if (this.filter.value && this.filter.value.id === filterId)
        {
          this.filter.setValue(null, { emitEvent: false });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Check if item from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  public selectionChange(e: any, dataItem: any, selectedIds: any[], emitSelectedService: boolean)
  {
    if (e.target.checked)
    {
      if (selectedIds.indexOf(dataItem.id) == -1)
      {
        selectedIds.push(dataItem.id);
        if (emitSelectedService)
        {
          this.selectedServices.emit(selectedIds);
        }
      }
    }
    else
    {
      const index = selectedIds.indexOf(dataItem.id);
      if (index >= 0)
      {
        selectedIds.splice(index, 1);
      }
    }
    this.refreshGridView();
  }

  public refreshGridView(): void
  {
    // Refresh grid accordingly
    this.gridView.data.forEach((x) =>
    {
      x.selectedResource = this.selectedResourcesIds.includes(x.id);
      x.selectedReferral = this.consentToReferIds.includes(x.id);
    });
  }

  /**
   * Check if the community resource is enabled
   *
   * @param communityResource Community Resource to check
   * @returns Returns true if the Community Resource is enabled and therefore searchable.
   */
  public isSearchable(communityResource: string): boolean
  {
    return this.searchableCommunityResources.indexOf(communityResource) !== -1;
  }

  /**
   * Filters the categories based on user input.
   */
  public filterCategories(filter: string): void
  {
    this.searchTerm = filter;
    let categories = cloneDeep(this.groupedServiceCategories);
    categories.forEach(d =>
    {
      const filteredItems = d.items.filter((item: any) => item.name.toLowerCase().includes(filter.toLowerCase()));
      d.items = filteredItems;
    });

    // Remove groups if there is no items
    categories = categories.filter(x => x.items.length > 0);

    // Set filtered categories
    this.categoriesFiltered = categories;
  }

  public filterOrganizations(filter: string): void
  {
    this.searchTerm = filter;
    this.organizationsFiltered = sortBy(this.allOrganizations
      .filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), ['name']);
  }

  public filterSubmittingOrganizations(filter: string): void
  {
    this.searchTerm = filter;
    this.submittingOrganizationsFiltered = sortBy(this.allSubmittingOrganizations
      .filter(item => item.name.toLowerCase().includes(filter.toLowerCase())), ['name']);
  }

  public filterResourceStatuses(filter: string): void
  {
    this.searchTerm = filter;
    this.resourceStatusesFiltered = sortBy(this.allResourceStatuses
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterServedCities(filter: string): void
  {
    this.searchTerm = filter;
    this.servedCitiesFiltered = sortBy(this.allServedCities
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterServedCounties(filter: string): void
  {
    this.searchTerm = filter;
    this.servedCountiesFiltered = sortBy(this.allServedCounties
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterServedZipCodes(filter: string): void
  {
    this.searchTerm = filter;
    this.servedZipCodesFiltered = sortBy(this.allServedZipCodes
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterTargetedDemographics(filter: string): void
  {
    this.searchTerm = filter;
    this.targetedDemographicsFiltered = sortBy(this.allTargetedDemographics
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterTargetedPopulations(filter: string): void
  {
    this.searchTerm = filter;
    this.targetedPopulationsFiltered = sortBy(this.allTargetedPopulations
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterLanguages(filter: string): void
  {
    this.searchTerm = filter;
    this.languagesFiltered = sortBy(this.allLanguages
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterEnhancedServices(filter: string): void
  {
    this.searchTerm = filter;
    this.enhancedServicesFiltered = this.allEnhancedServices
      .filter(user => user.displayName.toLowerCase().includes(filter.toLowerCase()));
  }

  public filterTaxonomies(filter: string): void
  {
    this.searchTerm = filter;
    this.taxonomiesFiltered = sortBy(this.allTaxonomies
      .filter(item => item.displayName.toLowerCase().includes(filter.toLowerCase())), ['displayName']);
  }

  public filterFilters(filter: string): void
  {
    let newFilters = [];
    this.searchTerm = filter;
    this.allFilters.forEach((x: any) =>
    {
      const groupedFilters = sortBy(x.items.filter((item: any) => item.name.toLowerCase().includes(filter.toLowerCase())), ['name']);
      newFilters = union(newFilters, groupedFilters);
    });

    // Set group translations
    newFilters.forEach((x: any) =>
    {
      if (x.category === 'CommunityServiceSystem')
      {
        x.categoryText = this.systemFiltersText;
      }
      else if (x.category === 'CommunityService')
      {
        x.categoryText = this.privateFiltersText;
      }
    });
    this.filtersFiltered = groupBy(newFilters, [{ field: 'categoryText' }]);
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.state = state;
    this.loadServices();
  }

  /**
   * Open the filter dialog
   */
  public openFilterScreen(e: PointerEvent): void
  {
    // Condition below is to avoid opening the Filter modal when hitting enter key on criteria.
    // We allow only if mouse clicking (pointerId === 1) or button have the focus and space or enter is pressed.
    if (e.pointerId === 1 || this.saveFilterButtonFocused) 
    {
      this.filterSelected = this.filter.value ? this.filter.value : undefined;
      this.filterDialogOpened = true;
    }
  }

  /**
   * Filter saved callback from the filter dialog
   *
   * @param savedFilter Filter object saved.
   */

  public filterSaved(savedFilter: Filter): void
  {
    this.loadFilters(savedFilter);
  }

  /**
   * Load all the community services filterd for the user.
   *
   * @param savedFilter If a savedFilter is provider, the new saved filter will be set on the drop-down.
   */
  public loadFilters(savedFilter: Filter = null): void
  {
    const user = this.authenticationService.currentUser;
    this.filterService.getCommunityServices(user.userName).subscribe({
      next: (filterList) =>
      {
        this.allFilters = groupBy(filterList, [{ field: 'category' }]);
        this.filterFilters('');
        if (savedFilter !== null)
        {
          this.filter.setValue(savedFilter, { emitEvent: false });
        }
      }
    });
  }

  /**
   * Grid column visibility changed.
   *
   * @param event Grid column visibility event.
   */
  public onVisibilityChange(event: ColumnVisibilityChangeEvent): void
  {
    event.columns.forEach((column: any) =>
    {
      const field = this.columnsConfig.find((col) => col.field === column.field);
      if (field !== undefined)
      {
        field.hidden = column.hidden;
      }
    });
    this.saveGrid();
  }

  /**
   * Grid onResize event.
   *
   * @param event Grid on resize column event.
   */
  public onResize(event: Array<GridResizeEvent>): void
  {
    event.forEach(item =>
    {
      const field = this.columnsConfig.find((col) => col.field === item.column.field);
      if (field !== undefined)
      {
        field.width = item.newWidth;
      }
    });
    this.saveGrid();
  }

  /**
   * Reorder grid column order event. -1 is to account for kendo-grid-checkbox-column
   *
   * @param e ColumnReorderEvent event
   */
  public onReorder(e: ColumnReorderEvent): void
  {
    const reorderedColumn = this.columnsConfig.splice(e.oldIndex - 1, 1);
    this.columnsConfig.splice(e.newIndex - 1, 0, ...reorderedColumn);
    this.saveGrid();
  }

  public pinServices(): void
  {
    this.memberService.savePinnedResources(this.memberId, this.selectedResourcesIds).subscribe({
      next: () =>
      {
        this.messageHandlerService.displaySuccess('PINNED-SERVICES');
        this.messengerService.sendMessage(new MessageSubject('reloadPinnedServices'));
      }
    });
  }

  public valueChange(currentSelection: any, previousSelection: any): void
  {
    if (currentSelection)
    {
      if (currentSelection.length === 0)
      {
        this[previousSelection] = [];
      }
      else
      {
        remove(this[previousSelection], (v: any) => currentSelection.findIndex(x => x.id === v.id) === -1);
      }
    }
  }

  public printServices(): void
  {
    this.isPrint = true;
    this.isEmail = false;
    this.isSMS = false;
    this.selectedEmailAddress = undefined;
    this.selectedPhoneNumber = undefined;
    this.servicesOpened = true;
  }

  public emailServices(): void
  {
    if (this.member && this.member.contact && this.member.contact.email !== null && this.member.contact.email !== '')
    {
      this.selectedEmailAddress = this.member.contact.email;
      this.selectedPhoneNumber = undefined;
      this.sendServiceByEmail();
    }
    else
    {
      this.emailForm = new UntypedFormGroup({ email: new UntypedFormControl(null, Validators.required) });
      this.emailConfirmationOpened = true;
      this.smsConfirmationOpened = false;
    }
  }

  public smsServices(): void
  {
    if (this.member && this.member.contact && this.member.contact.cellPhoneNumber !== null && this.member.contact.cellPhoneNumber !== '')
    {
      this.selectedPhoneNumber = this.member.contact.cellPhoneNumber;
      this.selectedEmailAddress = undefined;
      this.sendServiceBySms();
    }
    else
    {
      this.emailForm = new UntypedFormGroup({ sms: new UntypedFormControl(null, Validators.required) });
      this.smsConfirmationOpened = true;
      this.emailConfirmationOpened = false;
    }
  }

  public onSendEmail(): void
  {
    this.emailConfirmationOpened = false;
    this.smsConfirmationOpened = false;
    this.isPrint = false;
    this.selectedEmailAddress = this.email.value;
    this.isEmail = true;
    this.isSMS = false;
    this.servicesOpened = true;
  }

  public onSendSMS(): void
  {
    this.emailConfirmationOpened = false;
    this.smsConfirmationOpened = false;
    this.isPrint = false;
    this.selectedPhoneNumber = this.sms.value;
    this.isEmail = false;
    this.isSMS = true;
    this.servicesOpened = true;
  }

  /**
   * Check if category item is selected.
   *
   * @param filterItem Filter to check.
   * @param item Item value to check.
   */
  public isServiceCategoryFilterSelected(dataItem: any): boolean
  {
    return this.categories.value && this.categories.value.find((x: any) => x?.id === dataItem.id);
  }

  /**
   * Service category clicked
   * 
   * @param groupName Service Category clicked
   * @param event Checkbox click event
   */
  public groupCategoryClick(groupName: any, event: any): void
  {
    const availableItems = this.getServiceCategoryGroupItems(groupName);
    const selectedItems = cloneDeep(this.categories.value || []);
    if (!event.target.checked) 
    {
      const groupIds = availableItems.map((x: any) => x.id);
      this.categories.setValue(selectedItems.filter((x) => !groupIds.includes(x.id)));
    }
    else
    {
      availableItems.forEach((x: any) =>
      {
        if (!selectedItems.find((selected: any) => selected.id === x.id)) { selectedItems.push(x); }
      });
      this.categories.setValue(selectedItems);
    }
  }

  /**
   * Check if all items are selected.
   *
   * @param groupName Group to check.
   */
  public isServiceCategoryGroupChecked(groupName: string): boolean
  {
    const count = this.getServiceCategoryGroupCounts(groupName);
    return count.selected === count.available;
  }

  public clearAllSelected(): void
  {
    this.selectedResourcesIds = [];
    this.consentToReferIds = [];
    this.refreshGridView();
    this.selectedServices.emit([]);
  }

  /**
   * Calculate the service category selected and available count for the specified group name.
   * 
   * @param groupName Group name to return the count
   * @returns Object with selected and available.
   */
  private getServiceCategoryGroupCounts(groupName: string): { selected: number, available: number }
  {
    const availableItems = this.getServiceCategoryGroupItems(groupName);
    let length = 0;
    if (this.categories && this.categories.value)
    {
      this.categories.value.filter((s: any) => s.serviceTypeName === groupName)
        .forEach((x: any) => { if (availableItems.filter((c: any) => c.id === x.id)) { length++; } });
    }
    return { selected: length, available: availableItems.length };
  }

  /**
   * Get all available category for the provided group name
   * 
   * @param groupName Group name to return items
   * @returns Array Category items
   */
  private getServiceCategoryGroupItems(groupName: string): any
  {
    const foundGroup: any = this.groupedServiceCategories.find(x => x.value === groupName);
    return foundGroup && foundGroup.items ? foundGroup.items : [];
  }

  private sendServiceByEmail(): void
  {
    this.isPrint = false;
    this.isEmail = true;
    this.isSMS = false;
    this.servicesOpened = true;
  }

  private sendServiceBySms(): void
  {
    this.isPrint = false;
    this.isEmail = false;
    this.isSMS = true;
    this.servicesOpened = true;
  }

  /**
   * Load Community Services
   */
  private loadServices(): void
  {
    if (!this.showFilterBtnActions)
    {
      this.clearGrid();
      return;
    }

    // Set sort
    if (this.searchByDistanceEnabled)
    {
      const distanceSortFound = this.state.sort.findIndex(x => x.field === 'distance') > -1;
      if (!distanceSortFound)
      {
        this.state.sort.push({ field: 'distance', dir: 'asc' });
      }
      this.state.sort = this.state.sort.filter(x => x.dir !== undefined);
    }

    // Set filter
    this.state.filter.filters = [];

    if (this.allResourceStatuses.length === 0)
    {
      this.state.filter.filters.push({ field: 'resourceStatusCode', operator: 'neq', value: 'inactive', logic: 'and' });
    }

    if (this.isSearchable('NAME') && this.name.value)
    {
      this.state.filter.filters.push({ field: 'name', operator: 'contains', value: this.name.value, logic: 'and' });
    }

    // Apply any services exclusions from @Input() excludedServices
    if (this.excludedServices !== undefined && this.excludedServices.length > 0)
    {
      this.excludedServices.forEach((id: number) =>
      {
        this.state.filter.filters.push({ field: 'id', operator: 'neq', value: id, logic: 'and' });
      });
    }

    if (this.isSearchable('ACCEPTING_NEW_PATIENTS') && this.acceptingNewPatients.value !== null)
    {
      this.state.filter.filters.push({
        field: 'acceptingNewPatients', operator: 'eq',
        value: this.acceptingNewPatients.value, logic: 'and'
      });
    }

    if (this.isSearchable('ACCEPTING_SELF_REFERRALS') && this.acceptingSelfReferrals.value !== null)
    {
      this.state.filter.filters.push({
        field: 'acceptingSelfReferrals', operator: 'eq',
        value: this.acceptingSelfReferrals.value, logic: 'and'
      });
    }

    if (this.isSearchable('ABLE_TO_CONDUCT_SCREENINGS') && this.ableToConductScreenings.value !== null)
    {
      this.state.filter.filters.push({
        field: 'ableToConductScreenings', operator: 'eq',
        value: this.ableToConductScreenings.value, logic: 'and'
      });
    }

    if (this.isSearchable('INFORMATIONAL_ONLY') && this.informationalOnly.value !== null)
    {
      this.state.filter.filters.push({
        field: 'informationalOnly', operator: 'eq',
        value: this.informationalOnly.value, logic: 'and'
      });
    }

    if (this.isSearchable('ORGANIZATION') && this.organizations.value)
    {
      this.organizations.value.forEach((item: any) =>
      {
        this.state.filter.filters.push({ field: 'orgId', operator: 'eq', value: item.id, logic: 'or' });
      });
    }

    if (this.isSearchable('SUBMITTING_ORGANIZATION') && this.submittingOrganizations.value)
    {
      this.submittingOrganizations.value.forEach((item: any) =>
      {
        this.state.filter.filters.push({ field: 'submittingOrgId', operator: 'eq', value: item.id, logic: 'or' });
      });
    }

    if (this.isSearchable('SERVICE_CATEGORY') && this.categories.value)
    {
      this.categories.value.forEach((item: any) =>
      {
        this.state.filter.filters.push({ field: 'categoryId', operator: 'eq', value: item.id, logic: 'or' });
      });
    }

    if (this.isSearchable('AREAS_SERVED_CITIES') && this.servedCities.value)
    {
      const cities = this.servedCities.value.map((x: Dictionary) => x.id);
      if (cities.length > 0)
      {
        this.state.filter.filters.push({ field: 'cityIds', operator: 'eq', value: cities.join() });
      }
    }

    if (this.isSearchable('STATUS') && this.resourceStatuses.value)
    {
      const resourceStatuses = this.resourceStatuses.value.map((x: Dictionary) => x.id);
      if (resourceStatuses.length > 0)
      {
        this.state.filter.filters.push({ field: 'statusIds', operator: 'eq', value: resourceStatuses.join() });
      }
    }

    if (this.isSearchable('AREAS_SERVED_COUNTIES') && this.servedCounties.value)
    {
      const counties = this.servedCounties.value.map((x: Dictionary) => x.id);
      if (counties.length > 0)
      {
        this.state.filter.filters.push({ field: 'countyIds', operator: 'eq', value: counties.join() });
      }
    }

    if (this.isSearchable('AREAS_SERVED_ZIPCODES') && this.servedZipCodes.value)
    {
      const zipCodes = this.servedZipCodes.value.map((x: Dictionary) => x.id);
      if (zipCodes.length > 0)
      {
        this.state.filter.filters.push({ field: 'zipCodeIds', operator: 'eq', value: zipCodes.join() });
      }
    }

    if (this.isSearchable('TARGETED_DEMOGRAPHICS') && this.targetedDemographics.value)
    {
      const demographics = this.targetedDemographics.value.map((x: Dictionary) => x.id);
      if (demographics.length > 0)
      {
        this.state.filter.filters.push({ field: 'demographicIds', operator: 'eq', value: demographics.join() });
      }
    }

    if (this.isSearchable('TARGETED_POPULATIONS') && this.targetedPopulations.value)
    {
      const populations = this.targetedPopulations.value.map((x: Dictionary) => x.id);
      if (populations.length > 0)
      {
        this.state.filter.filters.push({ field: 'populationIds', operator: 'eq', value: populations.join() });
      }
    }

    if (this.isSearchable('LANGUAGES') && this.languages.value)
    {
      const languages = this.languages.value.map((x: Dictionary) => x.id);
      if (languages.length > 0)
      {
        this.state.filter.filters.push({ field: 'languageIds', operator: 'eq', value: languages.join() });
      }
    }

    if (this.isSearchable('TAXONOMIES') && this.taxonomies.value)
    {
      const taxonomies = this.taxonomies.value.map((x: Dictionary) => x.id);
      if (taxonomies.length > 0)
      {
        this.state.filter.filters.push({ field: 'taxonomyIds', operator: 'eq', value: taxonomies.join() });
      }
    }

    if (this.isSearchable('ENHANCED_SERVICE_ELIGIBILITY') && this.enhancedServices.value)
    {
      const enhancedServices = this.enhancedServices.value.map((x: Dictionary) => x.id);
      if (enhancedServices.length > 0)
      {
        this.state.filter.filters.push({ field: 'enhancedServiceIds', operator: 'eq', value: enhancedServices.join() });
      }
    }

    if (this.searchByDistanceEnabled && this.filterByDistance.value)
    {
      this.state.filter.filters.push({ field: 'distance', operator: 'lte', value: this.maxDistance.value, logic: 'or' });
    }

    // Set the patient address
    const address = this.searchByDistanceEnabled ? HelperUtils.formatAddress(this.member.address) : '';

    // Query the provider programs
    this.communityProviderProgramSearchList.queryByState(this.state, (value: CommunityProviderSearchResult) =>
    {
      value.data.forEach(p =>
      {
        this.communityProviderProgramSearchList.setCommunityServicesFields(p);
      });

      this.providers = value;

      this.gridView = {
        data: (value) ? this.gridData = value.data : this.gridData = [],
        total: (value) ? this.total = value.total : this.total = 0
      };

      this.refreshGridView();

      // Send Tag Manager events
      this.processTagManagerEvents();

    }, `&postal=${address}`);
  }

  /**
   * Load Organizations lookup
   */
  private loadOrganizations()
  {
    if (this.isSearchable('ORGANIZATION') || this.isSearchable('SUBMITTING_ORGANIZATION'))
    {
      this.organizationService.getCachedOrganizations().subscribe({
        next: (data) =>
        {
          this.allOrganizations = data;
          this.allSubmittingOrganizations = data.filter(o => o.submittingOrganization === true);
          this.filterOrganizations('');
          this.filterSubmittingOrganizations('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Load targeted demographics and populations lookups
   */
  private loadTargeted(): void
  {
    if (this.isSearchable('TARGETED_DEMOGRAPHICS'))
    {
      this.dictionaryService.getCachedTargetedDemographics().subscribe({
        next: (data) =>
        {
          this.allTargetedDemographics = data;
          this.filterTargetedDemographics('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }

    if (this.isSearchable('TARGETED_POPULATIONS'))
    {
      this.dictionaryService.getCachedTargetedPopulations().subscribe({
        next: (data) =>
        {
          this.allTargetedPopulations = data;
          this.filterTargetedPopulations('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Load languages lookup
   */
  private loadLanguages(): void
  {
    if (this.isSearchable('LANGUAGES'))
    {
      this.dictionaryService.getCachedLanguages().subscribe({
        next: (data) =>
        {
          this.allLanguages = data;
          this.filterLanguages('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  private loadEnhancedServices(): void
  {
    if (this.isSearchable('ENHANCED_SERVICE_ELIGIBILITY'))
    {
      this.dictionaryService.getCachedEnhancedServices().subscribe({
        next: (data) =>
        {
          this.allEnhancedServices = data;
          this.filterEnhancedServices('');
          const field = this.columnsConfig !== undefined && this.columnsConfig.find((col) => col.field === 'enhancedServicesName');
          if (field !== undefined)
          {
            field.hidden = this.enhancedServicesFiltered.length === 0;
          }
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Load taxonomies lookup
   */
  private loadTaxonomies(): void
  {
    if (this.isSearchable('TAXONOMIES'))
    {
      this.dictionaryService.getCachedProviderTaxonomies().subscribe({
        next: (data) =>
        {
          this.allTaxonomies = data;
          this.filterTaxonomies('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Load area served for cities, counties and zip codes.
   */
  private loadAreaServed(): void
  {
    if (this.isSearchable('AREAS_SERVED_CITIES'))
    {
      this.dictionaryService.getCachedAreaServedCities().subscribe({
        next: (data) =>
        {
          this.allServedCities = data;
          this.filterServedCities('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }

    if (this.isSearchable('AREAS_SERVED_COUNTIES'))
    {
      this.dictionaryService.getCachedAreaServedCounties().subscribe({
        next: (data) =>
        {
          this.allServedCounties = data;
          this.filterServedCounties('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }

    if (this.isSearchable('AREAS_SERVED_ZIPCODES'))
    {
      this.dictionaryService.getCachedAreaServedZipCodes().subscribe({
        next: (data) =>
        {
          this.allServedZipCodes = data;
          this.filterServedZipCodes('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  private loadResourceStatuses(): void
  {
    if (this.isSearchable('STATUS'))
    {
      this.dictionaryService.getCachedResourceStatuses().subscribe({
        next: (data) =>
        {
          this.allResourceStatuses = data;
          this.filterResourceStatuses('');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
  }

  /**
   * Loads the Service Categories and prepare the select control for the form.
   */
  private loadServiceCategories(): void
  {
    // Get community service categories
    this.communityServiceCategoryService.getCommunityServiceCategoriesList().subscribe({
      next: (categoriesData) =>
      {
        this.allServiceCategories = categoriesData.filter(x => x.active === true);
        this.allServiceCategories.forEach(d =>
        {
          d.serviceTypeName = d.serviceType.displayName;
          d.serviceCategoryName = d.name;
        });
        this.groupedServiceCategories = groupBy(this.allServiceCategories, [{ field: 'serviceTypeName' }]);
        this.groupedServiceCategories.forEach(d =>
        {
          const items = sortBy(d.items, ['name']);
          d.items = items;
        });
        this.filterCategories('');
        this.buildForm();
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Clear grid results
   */
  private clearGrid(): void
  {
    this.gridView = { data: [], total: 0 };
    this.providers.data = [];
    this.providers.total = 0;
    this.clearAllSelected();
  }

  /**
   * Load all UI selections based on saved filters.
   */
  private loadControlsFromFilters(): void
  {
    if (this.filter.value)
    {
      // Load controls from filter string
      const state = JSON.parse(this.filter.value.state);
      if (state.filter)
      {
        this.clearGrid();
        const listMapping = new Map<string, []>();

        state.filter.filters.forEach(f =>
        {
          let listToUse = [];

          // Proceed with adding object to list for filter ids
          switch (f.field)
          {
            case 'name':
              this.name.setValue(f.value, { emitEvent: false });
              break;
            case 'acceptingNewPatients':
              this.acceptingNewPatients.setValue(f.value, { emitEvent: false });
              break;
            case 'acceptingSelfReferrals':
              this.acceptingSelfReferrals.setValue(f.value, { emitEvent: false });
              break;
            case 'ableToConductScreenings':
              this.ableToConductScreenings.setValue(f.value, { emitEvent: false });
              break;
            case 'informationalOnly':
              this.informationalOnly.setValue(f.value, { emitEvent: false });
              break;
            case 'categoryId':
            case 'orgId':
            case 'submittingOrgId':
            case 'cityIds':
            case 'countyIds':
            case 'zipCodeIds':
            case 'demographicIds':
            case 'populationIds':
            case 'languageIds':
            case 'enhancedServiceIds':
            case 'taxonomyIds':
            case 'statusIds':
              if (!listMapping.has(f.field)) { listMapping.set(f.field, []); }

              if (f.field === 'categoryId')
              {
                listToUse = this.allServiceCategories;
              }
              else if (f.field === 'orgId')
              {
                listToUse = this.allOrganizations;
              }
              else if (f.field === 'submittingOrgId')
              {
                listToUse = this.allSubmittingOrganizations;
              }
              else if (f.field === 'cityIds')
              {
                listToUse = this.allServedCities;
              }
              else if (f.field === 'statusIds')
              {
                listToUse = this.allResourceStatuses;
              }
              else if (f.field === 'countyIds')
              {
                listToUse = this.allServedCounties;
              }
              else if (f.field === 'zipCodeIds')
              {
                listToUse = this.allServedZipCodes;
              }
              else if (f.field === 'demographicIds')
              {
                listToUse = this.allTargetedDemographics;
              }
              else if (f.field === 'populationIds')
              {
                listToUse = this.allTargetedPopulations;
              }
              else if (f.field === 'languageIds')
              {
                listToUse = this.allLanguages;
              }
              else if (f.field === 'enhancedServiceIds')
              {
                listToUse = this.allEnhancedServices;
              }
              else if (f.field === 'taxonomyIds')
              {
                listToUse = this.allTaxonomies;
              }
              this.addDictionaryToList(f.value, listToUse, listMapping.get(f.field));
              break;
          }
        });

        // Set control values
        this.categories.setValue(listMapping.get('categoryId'), { emitEvent: false });
        this.organizations.setValue(listMapping.get('orgId'), { emitEvent: false });
        this.submittingOrganizations.setValue(listMapping.get('submittingOrgId'), { emitEvent: false });
        this.servedCities.setValue(listMapping.get('cityIds'), { emitEvent: false });
        this.resourceStatuses.setValue(listMapping.get('statusIds'), { emitEvent: false });
        this.servedCounties.setValue(listMapping.get('countyIds'), { emitEvent: false });
        this.servedZipCodes.setValue(listMapping.get('zipCodeIds'), { emitEvent: false });
        this.targetedDemographics.setValue(listMapping.get('demographicIds'), { emitEvent: false });
        this.targetedPopulations.setValue(listMapping.get('populationIds'), { emitEvent: false });
        this.languages.setValue(listMapping.get('languageIds'), { emitEvent: false });
        this.enhancedServices.setValue(listMapping.get('enhancedServiceIds'), { emitEvent: false });
        this.taxonomies.setValue(listMapping.get('taxonomyIds'), { emitEvent: false });

        this.state.take = state.take;
        this.searchPrograms();
      }
    }
  }

  /**
   * Add the dictionary object to the proper map list.
   *
   * @param values Either an id number of a list of ids.
   * @param allCodes All dictionary codes
   * @param targetList Target list for adding the dictionary object.
   */
  private addDictionaryToList(values: any, allCodes: any, targetList: any): void
  {
    let listValues = [];
    // values could be a number or a list of numbers in a string
    if (typeof values === 'string')
    {
      listValues = values.split(',');
    }
    else
    {
      listValues.push(values);
    }
    listValues.forEach(value =>
    {
      const item = allCodes.find(x => x.id === parseInt(value, 10));
      if (item)
      {
        targetList.push(item);
      }
    });
  }

  /**
   * Grid columns initialization
   */
  private initGridColumns(): void
  {
    this.translateService.get([
      'SERVICE-NAME', 'ORGANIZATION', 'COMMUNITY-SERVICE-TYPE', 'COMMUNITY-SERVICE-CATEGORY', 'LANGUAGES', 'DISTANCE',
      'STREET', 'ADDRESS-LINE-2', 'CITY', 'STATE', 'POSTAL-CODE', 'FULL-ADDRESS',
      'ACCEPTING-NEW-PATIENTS', 'ACCEPTING-SELF-REFERRALS', 'ELIGIBILITY', 'COST', 'STATUS', 'ALIAS', 'ALERT-DESCRIPTION',
      'TAXONOMIES', 'ENHANCED-SERVICES-OFFERED', 'ABLE-TO-CONDUCT-SCREENINGS', 'SUBMITTING-ORGANIZATION', 'LAST-UPDATED', 'INFORMATIONAL-ONLY',
      'REFERRAL-CHANNEL'
    ]).subscribe({
      next: (res) =>
      {
        this.columnsConfig = [
          {
            field: 'name', title: res['SERVICE-NAME'], titleKeys: ['SERVICE-NAME'], width: 60, sortable: true, hidden: false,
            class: 'text-left'
          },
          {
            field: 'organization.name', title: res.ORGANIZATION, titleKeys: ['ORGANIZATION'], width: 80, sortable: true, hidden: false,
            class: 'text-left'
          },
          {
            field: 'serviceTypeName', title: res['COMMUNITY-SERVICE-TYPE'],
            titleKeys: ['COMMUNITY-SERVICE-TYPE'], width: 50, sortable: true, hidden: false, class: 'text-left'
          },
          {
            field: 'serviceCategoryName', title: res['COMMUNITY-SERVICE-CATEGORY'],
            titleKeys: ['COMMUNITY-SERVICE-CATEGORY'], width: 50, sortable: true, hidden: false, class: 'text-left'
          },
          {
            field: 'languagesName', title: res.LANGUAGES, titleKeys: ['LANGUAGES'], width: 60, sortable: false, hidden: false,
            class: 'ellipsis-text'
          },
          {
            field: 'enhancedServicesName', title: res['ENHANCED-SERVICES-OFFERED'], titleKeys: ['ENHANCED-SERVICES-OFFERED'], width: 60, sortable: false, hidden: false,
            class: 'ellipsis-text'
          },
          {
            field: 'distance', title: res.DISTANCE, titleKeys: ['DISTANCE'], width: 50, sortable: true, hidden: false
          },
          {
            field: 'formattedAddress', title: res['FULL-ADDRESS'], titleKeys: ['FULL-ADDRESS'], width: 80, sortable: false, hidden: true,
            class: 'text-left'
          },
          {
            field: 'address.streetLine', title: res.STREET, titleKeys: ['STREET'], width: 60, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'address.streetLineAdditional', title: res['ADDRESS-LINE-2'],
            titleKeys: ['ADDRESS-LINE-2'], width: 60, sortable: true, hidden: true, class: 'text-left'
          },
          {
            field: 'address.city', title: res.CITY, titleKeys: ['CITY'], width: 60, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'address.state.displayName', title: res.STATE, titleKeys: ['STATE'], width: 60, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'address.postalCode', title: res['POSTAL-CODE'], titleKeys: ['POSTAL-CODE'], width: 40, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'acceptingNewPatients', title: res['ACCEPTING-NEW-PATIENTS'].replace('{{patientLabel}}', this.patientLabel.patientLabel),
            titleKeys: ['ACCEPTING-NEW-PATIENTS'], width: 40, sortable: true, hidden: true, class: 'text-center'
          },
          {
            field: 'acceptingSelfReferrals', title: res['ACCEPTING-SELF-REFERRALS'], titleKeys: ['ACCEPTING-SELF-REFERRALS'],
            width: 40, sortable: true, hidden: true, class: 'text-center'
          },
          {
            field: 'ableToConductScreenings', title: res['ABLE-TO-CONDUCT-SCREENINGS'], titleKeys: ['ABLE-TO-CONDUCT-SCREENINGS'],
            width: 40, sortable: true, hidden: true, class: 'text-center'
          },
          {
            field: 'informationalOnly', title: res['INFORMATIONAL-ONLY'], titleKeys: ['INFORMATIONAL-ONLY'],
            width: 40, sortable: true, hidden: true, class: 'text-center'
          },
          {
            field: 'eligibility', title: res.ELIGIBILITY, titleKeys: ['ELIGIBILITY'], width: 40, sortable: true, hidden: true,
            class: 'ellipsis-text'
          },
          {
            field: 'cost', title: res.COST, titleKeys: ['COST'], width: 40, sortable: true, hidden: true,
            class: 'ellipsis-text'
          },
          {
            field: 'resourceStatusName', title: res.STATUS, titleKeys: ['STATUS'], width: 40, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'alias', title: res.ALIAS, titleKeys: ['ALIAS'], width: 40, sortable: true, hidden: true,
            class: 'text-left'
          },
          {
            field: 'alertDescription', title: res['ALERT-DESCRIPTION'],
            titleKeys: ['ALERT-DESCRIPTION'], width: 60, sortable: true, hidden: true, class: 'text-left'
          },
          {
            field: 'taxonomiesName', title: res.TAXONOMIES, titleKeys: ['TAXONOMIES'], width: 60, sortable: true, hidden: true,
            class: 'ellipsis-text'
          },
          {
            field: 'submittingOrganization.name', title: res['SUBMITTING-ORGANIZATION'], titleKeys: ['SUBMITTING-ORGANIZATION'], width: 60, sortable: true, hidden: true,
            class: 'ellipsis-text'
          },
          {
            field: 'lastModifiedOn', title: res['LAST-UPDATED'], titleKeys: ['LAST-UPDATED'], width: 60, sortable: true, hidden: true,
            class: 'ellipsis-text', format: '{0:d}', filter: 'date', dataType: 'date'
          },
          {
            field: 'channel.name', title: res['REFERRAL-CHANNEL'], titleKeys: ['REFERRAL-CHANNEL'], width: 60, sortable: true, hidden: true,
            class: 'ellipsis-text'
          },
        ];

        // If there is a previously saved grid settings, use it.
        const gridSettings: GridSettings = this.gridStatePersistingService.get('community-services-grid-settings');
        if (gridSettings !== null)
        {
          const loadedColumns = GridQueryUtils.setBaseColumnsSetting(this.translateService,
            JSON.stringify(gridSettings.columnsConfig), this.columnsConfig, this.patientLabel.patientLabel);
          this.columnsConfig = loadedColumns;
        }
      }
    });
  }

  /**
   * Persit grid columns into storage.
   */
  private saveGrid(): void
  {
    const gridConfig: GridSettings = { columnsConfig: this.columnsConfig, state: null };
    this.gridStatePersistingService.set('community-services-grid-settings', gridConfig);
  }

  /**
   * Initialize the member and related properties
   *
   * @param member Member in context.
   */
  private initMember(member: Member): void
  {
    this.member = member;
    this.searchByDistanceEnabled = member.address !== null;

    // Set the default categories provided at Component @Input() if any
    if (this.serviceCategories !== undefined && this.serviceCategories.length > 0)
    {
      const defaultCategories = this.allServiceCategories.filter(x => this.serviceCategories.includes(x.id));
      if (defaultCategories !== undefined && defaultCategories.length > 0)
      {
        this.categories.setValue(defaultCategories);
      }
    }
    this.loaded = true;
  }

  private setPageSize(): void
  {
    this.state.take = this.storageService.retrieve('community-services-page-size') || this.appConfigService.getConfig().pageSize;
  }

  /**
   * Processing Tag Manager push for search conditions.
   *
   * Currently supporting Categories, Cities, Counties and zip
   */
  private processTagManagerEvents(): void
  {
    if (this.tmService.enabled)
    {
      this.userService.getUserById(this.authenticationService.currentUser.id, false).subscribe({
        next: (user) =>
        {
          const name = user.organizationName;
          this.processTMSearchCriteria(name, this.categories, this.previousServiceCategories, 'serviceCategory');
          this.processTMSearchCriteria(name, this.resourceStatuses, this.previousResourceStatuses, 'resourceStatus');
          this.processTMSearchCriteria(name, this.taxonomies, this.previousTaxonomies, 'taxonomy');
          this.processTMSearchCriteria(name, this.servedCities, this.previousCitiesServed, 'cityServed');
          this.processTMSearchCriteria(name, this.servedCounties, this.previousCountiesServed, 'countyServed');
          this.processTMSearchCriteria(name, this.servedZipCodes, this.previousZipCodesServed, 'zipServed');
          this.processTMSearchCriteria(name, this.organizations, this.previousOrganizations, 'searchOrganization');
          this.processTMSearchCriteria(name, this.submittingOrganizations, this.previousSubmittingOrganizations, 'submittingOrganization');
          this.processTMSearchCriteria(name, this.languages, this.previousLanguages, 'language');
          this.processTMSearchCriteria(name, this.enhancedServices, this.previousEnhancedServices, 'enhancedService');
          this.processTMSearchCriteria(name, this.targetedPopulations, this.previousTargetedPopulations, 'targetedPopulation');
          this.processTMSearchCriteria(name, this.targetedDemographics, this.previousTargetedDemographics, 'targetedDemographic');
        }
      });
    }
  }

  /**
   * Send Community Search Criteria to Tag Manager.
   *
   * @param organizationName       Current logged in used organization name.
   * @param searchControl          Current criteria control
   * @param previousSelection      Previous selection list (used to send event just for new added selection)
   * @param tagManagerPropertyName Tag Manager property name (see TMEventSearchServices)
   */
  private processTMSearchCriteria(organizationName: string, searchControl: UntypedFormControl,
    previousSelection: any, tagManagerPropertyName: string): void
  {
    if (searchControl && searchControl.value && searchControl.value.length > 0)
    {
      searchControl.value.forEach(x =>
      {
        // If new search criteria is not already sent
        const found = previousSelection.find((y: any) => y.id === x.id);
        if (!found)
        {
          // Keep previous to avoid sending stats again
          previousSelection.push(x);

          // Category have name while dictionary is displayName
          const tmObj: TMEventSearchServices = { organization: organizationName };
          tmObj[tagManagerPropertyName] = x.name !== undefined ? x.name : x.displayName;
          this.tmService.sendTagManagerEvent(TMEventType.searchServices, tmObj);

          this.logger.info(TMEventType.searchServices, tmObj);
        }
      });
    }
  }

  private updateSplitterBar()
  {
    // do not run if no splitter is loaded
    if (!this.splitter?.nativeElement) { return; }

    const bar = this.splitter.nativeElement.querySelector('.k-splitbar');

    if (bar)
    {
      const pane = this.splitter.nativeElement.querySelector('.k-pane');
      const size = pane ? pane.offsetHeight : 0;

      bar.setAttribute('aria-valuenow', size.toString());
      bar.setAttribute('aria-valuemax', '9999');
    }
  }
}
